import { useTranslation } from "react-i18next";
import { Switch } from "@chakra-ui/react";
import SvgContainer from "src/components/SvgContainer";
import Menu from "src/components/Menu";
import MenuActionItem from "src/components/Menu/ActionItem";
import MenuActionBox from "src/components/Menu/ActionBox";
import MenuDivider from "src/components/Menu/Divider";
import MenuItemText from "src/components/Menu/ItemText";
import MenuItem from "src/components/Menu/Item";
import { useSelector } from "react-redux";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { DarkMode } from "src/styling/Modes";
import { ReactComponent as LullabiesIcon } from "src/modules/lullaby/icons/music-outlined.svg";
import { ReactComponent as SnapshotIcon } from "./icons/camera.svg";
import { ReactComponent as RecordIcon } from "./icons/recorder.svg";
import { ReactComponent as EveryNoiseIcon } from "./icons/everynoise-circle.svg";
import { ReactComponent as SettingsIcon } from "./icons/settings.svg";
import { ReactComponent as MuteIcon } from "./icons/muted-outlined.svg";
import { ReactComponent as RotateIcon } from "./icons/rotatecamera.svg";
import { ReactComponent as DetachIcon } from "./icons/detach-icon.svg";
import { colorMap } from "src/modules/colors";

export default function ParentStationMenu({
  isOpen,
  onClose,
  muted,
  mode,
  openSettings,
  openEveryNoise,
  openLullabyPlayer,
  enableLullabies,
  onOpen,
  toggleMute,
  canChangeCamera,
  takeSnapshot,
  startRecording,
  canRecordVideo,
  canTakeSnapshot,
  togglePurchaseFlow,
  enableDetach,
  detach,
  shouldRotateCamera,
  changeCamera,
  openCameraList
}) {
  const { t } = useTranslation();
  const isFreeVersion = useSelector(isFreemium);

  const handleCameraAction = () => {
    if (shouldRotateCamera) {
      changeCamera();
      return;
    }
    setTimeout(openCameraList, 1);
    onClose();
  };

  return (
    <DarkMode
      active={mode === "video"}
      adjuster={(colors) => ({ surfaceElevated1: colors.black70 })}
    >
      <Menu
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        iconFill={mode === "video" ? "white" : "mediumEmphasis"}
      >
        <MenuItem onClick={openEveryNoise} mt="8px">
          <SvgContainer
            size="20px"
            mr="16px"
            icon={EveryNoiseIcon}
            fill={colorMap.everyNoise}
          />
          <MenuItemText>{t("everyNoise")}</MenuItemText>
        </MenuItem>
        {enableLullabies && (
          <MenuItem onClick={openLullabyPlayer}>
            <SvgContainer
              size="20px"
              mr="16px"
              icon={LullabiesIcon}
              fill={colorMap.audioFiles}
            />
            <MenuItemText>{t("lullabies")}</MenuItemText>
          </MenuItem>
        )}
        {enableDetach && (
          <MenuItem
            onClick={() => {
              onClose();
              detach();
            }}
          >
            <SvgContainer
              size="20px"
              mr="16px"
              icon={DetachIcon}
              fill={colorMap.miniWindow}
            />
            <MenuItemText>{t("buttons.detach")}</MenuItemText>
          </MenuItem>
        )}
        <MenuItem onClick={openSettings}>
          <SvgContainer
            size="20px"
            mr="16px"
            icon={SettingsIcon}
            fill={colorMap.settings}
          />
          <MenuItemText>{t("settings")}</MenuItemText>
        </MenuItem>
        <MenuItem
          onClick={
            isFreeVersion
              ? () => {
                  togglePurchaseFlow("mute");
                  onClose();
                }
              : toggleMute
          }
          mb="8px"
          pr="12px"
        >
          <SvgContainer
            size="20px"
            mr="16px"
            icon={MuteIcon}
            fill={colorMap.mute}
          />
          <MenuItemText>{t("buttons.mute")}</MenuItemText>
          <Switch isChecked={muted} pointerEvents="none" />
        </MenuItem>
        {mode === "video" &&
        (canChangeCamera || canRecordVideo || canTakeSnapshot) ? (
          <>
            <MenuDivider mb="16px" />
            <MenuActionBox>
              {canChangeCamera && (
                <MenuActionItem
                  onClick={handleCameraAction}
                  icon={RotateIcon}
                  label={t("buttons.rotate")}
                />
              )}
              {canTakeSnapshot && (
                <MenuActionItem
                  onClick={() => {
                    isFreeVersion
                      ? togglePurchaseFlow("record")
                      : takeSnapshot();
                    onClose();
                  }}
                  icon={SnapshotIcon}
                  label={t("snapshot")}
                />
              )}
              {canRecordVideo && (
                <MenuActionItem
                  onClick={() => {
                    isFreeVersion
                      ? togglePurchaseFlow("record")
                      : startRecording();
                    onClose();
                  }}
                  icon={RecordIcon}
                  label={t("buttons.record")}
                />
              )}
            </MenuActionBox>
          </>
        ) : null}
      </Menu>
    </DarkMode>
  );
}
